/**
 * Ticket access control utilities
 * Provides functions to check user permissions for tickets
 */

import { api } from "utils/api";
import { DEPARTMENTS } from "utils/departmentUtils";

/**
 * Get the current user's login from localStorage
 * @returns {string|null} User login or null if not found
 */
export const getCurrentUserLogin = () => {
  try {
    const userString = localStorage.getItem("user");
    if (!userString) return null;

    const userData = JSON.parse(userString);
    return userData.login || null;
  } catch (error) {
    console.error("Error getting current user login:", error);
    return null;
  }
};

/**
 * Fetch the current user's ticket groups to determine which departments they can access
 * @returns {Promise<Object>} Object containing access info (hasAllAccess, accessibleDepartments, userLogin)
 */
export const fetchUserTicketAccess = async () => {
  try {
    const userLogin = getCurrentUserLogin();

    if (!userLogin) {
      return { hasAllAccess: false, accessibleDepartments: [] };
    }

    // Fetch user's ticket groups from ticket_users table
    const ticketGroupsResponse = await api.post("/db/ticket_users/filter", {
      filters: {
        login: userLogin,
      },
      columns: ["ticket_user_id", "login", "ticket_group"],
    });

    if (!Array.isArray(ticketGroupsResponse) || ticketGroupsResponse.length === 0) {
      console.warn(`No ticket groups found for user ${userLogin}`);
      return { hasAllAccess: false, accessibleDepartments: [], userLogin };
    }

    // Check if user has the "All" group which grants access to all tickets
    const hasAllAccess = ticketGroupsResponse.some((group) => group.ticket_group === "All");

    // If user has all access, we don't need departments list
    if (hasAllAccess) {
      return { hasAllAccess: true, accessibleDepartments: [], userLogin };
    }

    // Extract department names (ticket_group) that the user has access to
    const accessibleDepartments = ticketGroupsResponse.map((group) => group.ticket_group);

    return {
      hasAllAccess: false,
      accessibleDepartments,
      userLogin,
    };
  } catch (error) {
    console.error("Error fetching user ticket access:", error);
    return { hasAllAccess: false, accessibleDepartments: [], userLogin: getCurrentUserLogin() };
  }
};

/**
 * Check if the current user has permission to view a ticket based on its department
 * @param {string} department - The department of the ticket
 * @returns {Promise<boolean>} Whether the user has permission to view this ticket
 */
export const hasTicketAccess = async (department) => {
  try {
    const { hasAllAccess, accessibleDepartments } = await fetchUserTicketAccess();

    // If user has all access, they can view any ticket
    if (hasAllAccess) {
      return true;
    }

    // Otherwise, check if user has access to the specific department
    return accessibleDepartments.includes(department);
  } catch (error) {
    console.error("Error checking ticket access:", error);
    return false;
  }
};

/**
 * Get filters to apply when fetching tickets based on user permissions
 * @returns {Promise<Object>} Filters object to use with the API
 */
export const getTicketFilters = async () => {
  const { hasAllAccess, accessibleDepartments } = await fetchUserTicketAccess();

  // If user has all access, no filters needed
  if (hasAllAccess) {
    return {};
  }

  // If user has specific department access, filter by those departments
  if (accessibleDepartments.length > 0) {
    return { department__in: accessibleDepartments };
  }

  // If user has no department access, return impossible filter to ensure no tickets are returned
  return { ticket_id: -1 }; // This will never match an actual ticket
};

/**
 * Check if the current user can create or update tickets in a specific department
 * @param {string} department - The department to check
 * @returns {Promise<boolean>} Whether the user can manage tickets in this department
 */
export const canManageTicketsInDepartment = async (department) => {
  // For now, use the same access control as viewing tickets
  // In a more complex system, this could check for additional permissions
  return await hasTicketAccess(department);
};

/**
 * Get list of departments that the current user can manage tickets in
 * @returns {Promise<string[]>} List of department values the user can manage
 */
export const getManageableDepartments = async () => {
  const { hasAllAccess, accessibleDepartments } = await fetchUserTicketAccess();

  if (hasAllAccess) {
    return DEPARTMENTS.map((dept) => dept.value);
  }

  // If user has specific department access, return those departments
  return accessibleDepartments;
};

/**
 * Check if the current user can view the tickets list at all
 * @returns {Promise<boolean>} Whether the user has any ticket access
 */
export const hasAnyTicketAccess = async () => {
  const { hasAllAccess, accessibleDepartments } = await fetchUserTicketAccess();
  return hasAllAccess || accessibleDepartments.length > 0;
};

export default {
  getCurrentUserLogin,
  fetchUserTicketAccess,
  hasTicketAccess,
  getTicketFilters,
  canManageTicketsInDepartment,
  getManageableDepartments,
  hasAnyTicketAccess,
};
